import React, { useState, useEffect, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  TableColumn,
  StandardizedTable,
  formatNumber,
  formatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { useAccount } from "@/contexts/account.context";
import { useDrilldownOptional } from "@/features/drilldown";
import { getSourceAnalysisCardApiV1SourceAnalysisCardGet } from "@/backend-sdk/client/sdk.gen";
import { rodapeDeMidiaPaga } from "@/lib/paid-media-total";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import {
  MIDIA_PAGA,
  type RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";
import type {
  GetSourceAnalysisCardApiV1SourceAnalysisCardGetData,
  SourceAnalysisCardResponse,
} from "@/backend-sdk/client/types.gen";

type SourceAnalysisQuery =
  GetSourceAnalysisCardApiV1SourceAnalysisCardGetData["query"];
type SourceAnalysisCrossFilterKey = Extract<
  keyof SourceAnalysisQuery,
  `filter_${string}`
>;
type SourceAnalysisCrossFilters = Pick<
  SourceAnalysisQuery,
  SourceAnalysisCrossFilterKey
>;

const getSourceAnalysisCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<SourceAnalysisCrossFilters> => ({
  filter_search_term: filters?.filter_search_term,
  filter_device: filters?.filter_device,
  filter_region: filters?.filter_region,
  filter_city: filters?.filter_city,
  filter_category: filters?.filter_category,
  filter_product: filters?.filter_product,
  filter_date: filters?.filter_date,
});

/**
 * A mídia paga de uma linha: o investimento da linha "Meta Ads" só existe com o Meta Ads, e o
 * da "Google Ads", só com o Google Ads. O rodapé soma as duas, e qualquer uma basta.
 */
const midiaDaLinha = (linha: TableRow | undefined): RequisitoDeFonte => {
  if (linha?.origem === "Meta Ads") return "meta_ads";
  if (linha?.origem === "Google Ads") return "google_ads";
  return MIDIA_PAGA;
};

/**
 * De onde sai cada coluna: sessões, pedidos e receita vêm do GA4; o gasto, da mídia da linha;
 * CPS, ROAS e % Marketing dividem um pelo outro e precisam dos dois.
 */
type BaseDaColuna = "ga4" | "midia" | "ga4ComMidia";

const fontesDaCelula = (
  base: BaseDaColuna,
  linha: TableRow | undefined,
): readonly RequisitoDeFonte[] => {
  if (base === "ga4") return ["ga4"];
  if (base === "midia") return [midiaDaLinha(linha)];
  return ["ga4", midiaDaLinha(linha)];
};

const AnaliseOrigemTable: React.FC = () => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const drilldown = useDrilldownOptional();
  const [sourceAnalysisData, setSourceAnalysisData] =
    useState<SourceAnalysisCardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drilldown: click handler para linhas
  const handleRowClick = useCallback(
    (row: TableRow, _index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;
      const value = String(row.origem ?? "");
      if (!value) return;
      const multiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("ad_platform", value, value, multiSelect);
    },
    [drilldown],
  );

  // Drilldown: verificar se linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, _index: number): boolean => {
      if (!drilldown) return false;
      const value = String(row.origem ?? "");
      return drilldown.isSelected("ad_platform", value);
    },
    [drilldown],
  );

  const renderND = (base: BaseDaColuna, linha: TableRow | undefined) => (
    <ValorIndisponivel
      className="text-muted-foreground"
      fontes={fontesDaCelula(base, linha)}
      erro={error !== null}
    />
  );

  const isEmptyOrZero = (v: unknown) =>
    v === null || v === undefined || Number(v) === 0;

  const comparisonActive = isComparisonEnabled(globalFilter);

  const colunas: TableColumn[] = [
    {
      key: "sessoes",
      header: "Sessões",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value) ? renderND("ga4", linha) : formatNumber(value),
    },
    {
      key: "sessoesTotal",
      header: "% Sessões Total",
      type: "percent" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value)
          ? renderND("ga4", linha)
          : `${(Number(value) || 0).toFixed(2)}%`,
    },
    {
      key: "percentualVariacao",
      header: "% Δ",
      type: "percent" as const,
      visible: comparisonActive,
      // O rodapé não tem variação: ele é o agregado das duas origens, e comparar o agregado
      // com o período anterior é outra conta, que a rota não publica.
      render: (value: number, linha, _indice, ehTotal?: boolean) =>
        ehTotal ? null : value === null || value === undefined ? (
          renderND("ga4", linha)
        ) : (
          <TrendIndicator value={Number(value)} enabled={true} />
        ),
    },
    {
      key: "cps",
      header: "CPS",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value)
          ? renderND("ga4ComMidia", linha)
          : Number(value || 0).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
    {
      key: "pedidos",
      header: "Pedidos",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value) ? renderND("ga4", linha) : String(value),
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      type: "percent" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value)
          ? renderND("ga4", linha)
          : `${(Number(value) || 0).toFixed(2)}%`,
    },
    {
      key: "tm",
      header: "TM",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value) ? renderND("ga4", linha) : formatCurrency(value),
    },
    {
      key: "investimento",
      header: "Investimento",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value) ? renderND("midia", linha) : formatCurrency(value),
    },
    {
      key: "receitaTotal",
      header: "Receita total",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value) ? renderND("ga4", linha) : formatCurrency(value),
    },
    {
      key: "roas",
      header: "ROAS",
      type: "number" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value)
          ? renderND("ga4ComMidia", linha)
          : Number(value || 0).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
    {
      key: "percentualVariacaoRoas",
      header: "% Δ",
      type: "percent" as const,
      visible: comparisonActive,
      render: (value: number, linha, _indice, ehTotal?: boolean) =>
        ehTotal ? null : value === null || value === undefined ? (
          renderND("ga4ComMidia", linha)
        ) : (
          <TrendIndicator value={Number(value)} enabled={true} />
        ),
    },
    {
      key: "marketing",
      header: "% Marketing",
      type: "percent" as const,
      render: (value: number, linha) =>
        isEmptyOrZero(value)
          ? renderND("ga4ComMidia", linha)
          : `${Number(value) || 0}%`,
    },
  ];

  // Função para buscar dados do Source Analysis
  const fetchSourceAnalysisData = useCallback(async () => {
    if (!globalFilter) return;

    try {
      setLoading(true);
      setError(null);

      const startDate = formatDateToISO(globalFilter.dateRange.startDate);
      const endDate = formatDateToISO(globalFilter.dateRange.endDate);
      const customerUuid =
        selectedAccount?.customer_uuid ||
        selectedAccount?.id ||
        localStorage.getItem("active_customer_uuid") ||
        undefined;

      if (!customerUuid) return;

      const { data } = await getSourceAnalysisCardApiV1SourceAnalysisCardGet({
        query: {
          start_date: startDate,
          end_date: endDate,
          period: globalFilter.period,
          comparison: globalFilter.comparison?.type || "previous_period",
          comparison_label: globalFilter.comparison?.label,
          custom_start_date:
            globalFilter.period === "custom" &&
            globalFilter.customDateRange?.startDate
              ? formatDateToISO(globalFilter.customDateRange.startDate)
              : undefined,
          custom_end_date:
            globalFilter.period === "custom" &&
            globalFilter.customDateRange?.endDate
              ? formatDateToISO(globalFilter.customDateRange.endDate)
              : undefined,
          customer_uuid: customerUuid,
          ...getSourceAnalysisCrossFilters(drilldown?.apiFilters),
        },
        throwOnError: true,
      });

      setSourceAnalysisData(data);
      console.log("✅ Dados do Source Analysis carregados:", data);
    } catch (err) {
      console.error("❌ Erro ao buscar dados do Source Analysis:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Erro ao carregar dados do Source Analysis",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, globalFilter, drilldown]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchSourceAnalysisData();
  }, [fetchSourceAnalysisData]);

  // Preparando os dados para Meta Ads
  const metaAdsData = sourceAnalysisData
    ? [
        {
          sessoes: sourceAnalysisData.meta_ads.sessions,
          sessoesTotal: sourceAnalysisData.meta_ads.sessions_total_percentage,
          percentualVariacao:
            sourceAnalysisData.meta_ads.sessions_change_percentage ?? 0,
          cps: sourceAnalysisData.meta_ads.cps,
          pedidos: sourceAnalysisData.meta_ads.transactions,
          txConversao: sourceAnalysisData.meta_ads.conversion_rate,
          tm: sourceAnalysisData.meta_ads.avg_order_value,
          investimento: sourceAnalysisData.meta_ads.investment,
          receitaTotal: sourceAnalysisData.meta_ads.revenue,
          roas: sourceAnalysisData.meta_ads.roas,
          percentualVariacaoRoas:
            sourceAnalysisData.meta_ads.roas_change_percentage ?? 0,
          marketing: sourceAnalysisData.meta_ads.marketing_percentage,
        },
      ]
    : [];

  // Preparando os dados para Google Ads
  const googleAdsData = sourceAnalysisData
    ? [
        {
          sessoes: sourceAnalysisData.google_ads.sessions,
          sessoesTotal: sourceAnalysisData.google_ads.sessions_total_percentage,
          percentualVariacao:
            sourceAnalysisData.google_ads.sessions_change_percentage ?? 0,
          cps: sourceAnalysisData.google_ads.cps,
          pedidos: sourceAnalysisData.google_ads.transactions,
          txConversao: sourceAnalysisData.google_ads.conversion_rate,
          tm: sourceAnalysisData.google_ads.avg_order_value,
          investimento: sourceAnalysisData.google_ads.investment,
          receitaTotal: sourceAnalysisData.google_ads.revenue,
          roas: sourceAnalysisData.google_ads.roas,
          percentualVariacaoRoas:
            sourceAnalysisData.google_ads.roas_change_percentage ?? 0,
          marketing: sourceAnalysisData.google_ads.marketing_percentage,
        },
      ]
    : [];

  // Error state
  if (error) {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardHeader>
          <CardTitle>ANÁLISE MÍDIA PAGA</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="text-center py-8">
            <p className="text-red-500 mb-2">Erro ao carregar dados</p>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const unifiedColumns: TableColumn[] = [
    {
      key: "origem",
      header: "Origem",
      type: "text" as const,
      align: "left",
      alwaysVisible: true,
      width: 200,
    },
    ...colunas,
  ];

  const unifiedData = [
    ...(metaAdsData.length > 0
      ? [{ origem: "Meta Ads", ...metaAdsData[0] }]
      : []),
    ...(googleAdsData.length > 0
      ? [{ origem: "Google Ads", ...googleAdsData[0] }]
      : []),
  ];

  return (
    <StandardizedTable
      title="ANÁLISE MÍDIA PAGA"
      description="Comparativo consolidado entre Meta Ads e Google Ads"
      columns={unifiedColumns}
      data={unifiedData}
      // A espera fica dentro da tabela, que mantém as linhas de antes enquanto a nova busca não volta.
      loading={loading}
      computeTotal={rodapeDeMidiaPaga}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
    />
  );
};

export default AnaliseOrigemTable;
